import React, { useState, useEffect } from "react";
import Image from "theme/components/atoms/Image";
import classNames from "classnames";
import GalleryNavigation from "./GalleryNavigation";
import GalleryNavigationSkeleton from "./GalleryNavigationSkeleton";
import { useSliderState } from "theme/components/organisms/Slider/Slider";
import Spinner from "theme/components/atoms/Spinner";
import Slider from "theme/components/organisms/Slider";
import Lightbox from "theme/components/organisms/Lightbox";
import { defineMessages, useIntl } from "react-intl";

const messages = defineMessages({
  goToSlide: {
    id: "modules.ProductView.Gallery.goToSlide",
    defaultMessage: "Go to image {number}",
  },
  zoom: {
    id: "modules.ProductView.Gallery.zoom",
    defaultMessage: "Zoom on the image",
  },
});

const GalleryWithCarousel = ({
  loading,
  name,
  images,
  numberOfVisibleThumbnails,
  imageSizes,
  thumbSizes,
  enableZoomOnImage = true,
}) => {
  const intl = useIntl();
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isLoadingLightbox, setIsLoadingLightbox] = useState(false);

  useEffect(() => {
    enableZoomOnImage && Lightbox.preload();
  }, [enableZoomOnImage]);
  const length = images?.length;
  const sliderProps = useSliderState(length);

  return (
    <div className="product-gallery">
      <div className="product-gallery__main">
        <Slider {...sliderProps}>
          {images.map((image, index) => (
            <Image
              key={image.id}
              src={image.file}
              alt={image.alt}
              format="medium"
              appearance="full"
              priority={index === 0}
              sizes={imageSizes}
            />
          ))}
        </Slider>
        {enableZoomOnImage && (
          <button
            className={classNames("product-gallery__zoom", {
              "product-gallery__zoom--loading": isLoadingLightbox,
            })}
            onClick={() => {
              setIsLightboxOpen(true);
              setIsLoadingLightbox(true);
            }}
            title={intl.formatMessage(messages.zoom)}
          >
            <span className="sr-only">{intl.formatMessage(messages.zoom)}</span>
            {isLoadingLightbox && <Spinner />}
          </button>
        )}

        {isLightboxOpen && (
          <Lightbox
            mainImageSrc={images[sliderProps.state.index].file}
            imagesSrc={images.map(({ file }) => file)}
            onAfterOpen={() => {
              setIsLoadingLightbox(false);
            }}
            onCloseRequest={() => {
              setIsLightboxOpen(false);
            }}
          />
        )}

        {length > 1 ? (
          <div className="product-gallery__nav">
            {new Array(length).fill(null).map((_, index) => (
              <button
                className={classNames("product-gallery__button", {
                  "product-gallery__button--current":
                    index === sliderProps.state.nextIndex,
                })}
                onClick={() => sliderProps.goTo(index)}
                key={index}
              >
                {intl.formatMessage(messages.goToSlide, {
                  number: index + 1,
                })}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {loading ? (
        <aside className="product-gallery__aside">
          <GalleryNavigationSkeleton
            numberOfVisibleThumbnails={numberOfVisibleThumbnails}
          />
        </aside>
      ) : length > 1 ? (
        <aside className="product-gallery__aside">
          <GalleryNavigation
            name={name}
            images={images}
            numberOfVisibleThumbnails={numberOfVisibleThumbnails}
            onImageClick={({ id }) => {
              const index = images.findIndex((image) => image.id === id);
              if (sliderProps.state.index !== index) {
                sliderProps.goTo(index);
              }
            }}
            imageSizes={thumbSizes}
          />
        </aside>
      ) : null}
    </div>
  );
};
export default GalleryWithCarousel;
